import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  Row,
  type Table as ReactTable,
  TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  CSSProperties,
  DetailedHTMLProps,
  memo,
  ReactNode,
  RefAttributes,
  TableHTMLAttributes,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Menu, MenuTrigger } from 'react-aria-components';

import { merge } from '@/components';
import { Button, Flex, MenuItem, MenuItemDelete, Popover, Text } from '@/components/core';
import { CopyIcon, DragHandleIcon, OverflowIcon } from '@/components/icons';
import { useAutoSaveColumnSizes } from '@/hooks';
import { Id, TableRowProps } from '@/utils';

export type RowOptions<TRow> = {
  options?: {
    onAction: (row: Row<TRow>) => void;
    children: ReactNode;
  }[];
  duplicateRowAction?: (row: Row<TRow>) => void;
  deleteRowAction?: (row: Row<TRow>) => void;
};

type DraggableRowProps<TRow> = TableRowProps &
  RowOptions<TRow> & {
    row: Row<TRow>;
    isInnerTable?: boolean;
    hideDragHandle?: boolean;
    hideRowOptions?: boolean;
    props?: {
      cell?: React.TdHTMLAttributes<HTMLTableCellElement>;
      row?: React.HTMLAttributes<HTMLTableRowElement>;
    };
    innerRef?: (el: HTMLTableRowElement | null) => void;
  };

const IDraggableRow = <TRow extends Id>({
  row,
  isInnerTable,
  hideDragHandle,
  hideRowOptions,
  options,
  duplicateRowAction,
  deleteRowAction,
  className,
  innerRef,
  props,
  style: incomingStyle,
  ...rest
}: DraggableRowProps<TRow>) => {
  const { attributes, listeners, transform, transition, setNodeRef, isDragging } = useSortable({
    id: row.id,
  });

  const style: CSSProperties = {
    ...incomingStyle,
    transform: [CSS.Transform.toString(transform), incomingStyle?.transform].filter(Boolean).join(' '),
    transition: [transition, incomingStyle?.transition].filter(Boolean).join(' '),
  };

  const { className: cellClassName, ...cellProps } = props?.cell ?? {};

  return (
    <tr
      className={merge(
        isDragging ? 'z-10 opacity-80' : 'z-0 opacity-100',
        isInnerTable ? 'group/row-inner' : 'group/row-outer',
        className,
        props?.row?.className
      )}
      ref={(el) => {
        setNodeRef(el);
        if (el) {
          innerRef?.(el);
        }
      }}
      style={style}
      {...rest}
    >
      {!hideDragHandle ? (
        <td className="flex w-0 items-center">
          <button
            {...attributes}
            {...listeners}
            className="group/button px-xs flex -translate-x-[100%] cursor-pointer"
            tabIndex={-1}
          >
            <DragHandleIcon
              className={merge(
                'text-transparent',
                isInnerTable ? 'group-hover/row-inner:!text-gray-400' : 'group-hover/row-outer:!text-gray-400'
              )}
            />
          </button>
        </td>
      ) : null}
      {row.getVisibleCells().map((cell, index) => (
        <td
          className={cellClassName}
          key={cell.id}
          style={{ width: cell.column.getSize() }}
          aria-colindex={index}
          {...cellProps}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </td>
      ))}
      {!hideRowOptions ? (
        <td className="flex w-0 items-center">
          <MenuTrigger>
            <Button className="px-xs p-0" variant="plain" excludeFromTabOrder>
              <OverflowIcon
                className={merge(
                  'text-transparent',
                  isInnerTable ? 'group-hover/row-inner:!text-gray-400' : 'group-hover/row-outer:!text-gray-400'
                )}
              />
            </Button>
            <Popover>
              <Menu className="outline-none">
                {options?.map((option, i) => (
                  <MenuItem key={i} onAction={() => option.onAction(row)}>
                    {option.children}
                  </MenuItem>
                ))}
                {duplicateRowAction ? (
                  <MenuItem onAction={() => duplicateRowAction(row)}>
                    <CopyIcon />
                    <Text>Duplicate</Text>
                  </MenuItem>
                ) : null}
                {deleteRowAction ? <MenuItemDelete onAction={() => deleteRowAction(row)} /> : null}
              </Menu>
            </Popover>
          </MenuTrigger>
        </td>
      ) : null}
    </tr>
  );
};

export const DraggableRow = memo(IDraggableRow) as typeof IDraggableRow;

export type TableRef<TRow> = {
  table: ReactTable<TRow>;
  remountSortableContext: () => void;
};

export type TableProps<TRow> = Omit<DetailedHTMLProps<TableHTMLAttributes<HTMLTableElement>, HTMLTableElement>, 'ref'> &
  RowOptions<TRow> &
  RefAttributes<TableRef<TRow>> & {
    columns: ColumnDef<TRow>[];
    data: TRow[];
    isInnerTable?: boolean;
    onRowIndexChange?: (oldIndex: number, newIndex: number) => void;
    hideHeader?: boolean;
    hideRowOptions?: boolean;
    hideDragHandle?: boolean;
    isResizeable?: boolean;
    props?: {
      row?: TableRowProps;
      cell?: React.TdHTMLAttributes<HTMLTableCellElement>;
      header?: React.ThHTMLAttributes<HTMLElement>;
      headerCell?: React.ThHTMLAttributes<HTMLTableCellElement>;
      container?: React.HTMLAttributes<HTMLElement>;
    };
  };

const ITable = <TRow extends Id>({
  data,
  isInnerTable,
  columns,
  onRowIndexChange,
  hideHeader,
  hideRowOptions,
  hideDragHandle,
  isResizeable,
  options,
  duplicateRowAction,
  deleteRowAction,
  className,
  props,
  ref,
  ...rest
}: TableProps<TRow>) => {
  const [sortableContextKey, setSortableContextKey] = useState(0);

  const dataIds = useMemo<UniqueIdentifier[]>(() => data?.map(({ id }) => id), [data]);

  const reactTableProps: TableOptions<TRow> = {
    columns,
    data: data,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  };

  if (isResizeable) {
    reactTableProps.enableColumnResizing = true;
    reactTableProps.columnResizeMode = 'onChange';
    reactTableProps.columnResizeDirection = 'ltr';
  }

  const table = useReactTable(reactTableProps);
  const { rows } = table.getRowModel();
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => 50,
    getScrollElement: () => tableContainerRef.current,
    overscan: 3,
  });

  useAutoSaveColumnSizes({ table, enabled: isResizeable });

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (active && over && active.id !== over.id) {
        const oldIndex = dataIds.indexOf(active.id);
        const newIndex = dataIds.indexOf(over.id);
        onRowIndexChange?.(oldIndex, newIndex);
      }
    },
    [dataIds, onRowIndexChange]
  );

  useImperativeHandle(ref, () => {
    return {
      remountSortableContext() {
        setSortableContextKey(sortableContextKey + 1);
      },
      table,
    };
  }, [sortableContextKey, table]);

  const sensors = useSensors(useSensor(MouseSensor, {}), useSensor(TouchSensor, {}), useSensor(KeyboardSensor, {}));
  const { className: rowClassName, ...rowProps } = props?.row ?? {};
  const { className: headerClassName, ...headerProps } = props?.header ?? {};
  const { className: headerCellClassName, ...headerCellProps } = props?.headerCell ?? {};

  return (
    <Flex
      col
      ref={tableContainerRef}
      {...props?.container}
      className={merge('relative w-full overflow-y-auto', props?.container?.className)}
    >
      <DndContext
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleDragEnd}
        onDragStart={() => setSortableContextKey((prev) => prev + 1)}
        sensors={sensors}
      >
        <table
          className={merge('group/table', className)}
          style={isResizeable ? { width: table.getCenterTotalSize() } : undefined}
          {...rest}
        >
          {!hideHeader ? (
            <thead className={merge('sticky top-0 z-1')}>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className={merge('flex', headerClassName)} {...headerProps}>
                  {!hideDragHandle ? <th className="max-w-0!" {...headerCellProps} aria-colindex={0} /> : null}

                  {headerGroup.headers.map((header, index) => (
                    <th
                      className={merge('group/th relative text-left', headerCellClassName)}
                      colSpan={header.colSpan}
                      style={{ width: header.getSize() }}
                      key={header.id}
                      aria-colindex={index}
                      {...headerCellProps}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}

                      {isResizeable ? (
                        <div
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          className="absolute top-0 right-0 z-50 h-full w-4 translate-x-2 cursor-col-resize touch-none select-none"
                        />
                      ) : null}
                    </th>
                  ))}
                  {!hideRowOptions ? <th className="w-0" {...headerCellProps} /> : null}
                </tr>
              ))}
            </thead>
          ) : null}
          <tbody style={{ position: 'relative', height: rowVirtualizer.getTotalSize() }}>
            {/* https://github.com/clauderic/dnd-kit/issues/1513 */}
            {/* https://github.com/clauderic/dnd-kit/issues/1417 */}
            <SortableContext key={sortableContextKey} items={dataIds} strategy={verticalListSortingStrategy}>
              {rowVirtualizer.getVirtualItems().map((virtualRow, index) => {
                const row = rows[virtualRow.index];

                return (
                  <DraggableRow
                    style={{
                      position: 'absolute',
                      top: virtualRow.start,
                    }}
                    data-index={virtualRow.index}
                    aria-rowindex={index}
                    key={row.id}
                    innerRef={rowVirtualizer.measureElement}
                    className={merge(rowClassName, 'flex')}
                    row={row}
                    isInnerTable={isInnerTable}
                    hideDragHandle={hideDragHandle}
                    hideRowOptions={hideRowOptions}
                    options={options}
                    duplicateRowAction={duplicateRowAction}
                    deleteRowAction={deleteRowAction}
                    props={{
                      cell: { ...props?.cell },
                      row: { ...props?.row },
                    }}
                    {...rowProps}
                  />
                );
              })}
            </SortableContext>
          </tbody>
        </table>
      </DndContext>
    </Flex>
  );
};

export const Table2 = memo(ITable) as typeof ITable;
